import React, { useEffect, useRef } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { SectionContainer, StickyCTABar } from "component-library";
import { IAudioModel, IVideoModel } from "shared-types";
import { useTranslation } from "i18n";
import { captureExceptionEvent } from "shared-services";
import { UserRoleManager, UserRoles } from "app/core";
import {
  Breadcrumbs,
  IBreadcrumbsContent,
} from "components/common/Breadcrumbs";
import { IBreadcrumbsContentKeys } from "components/common/Breadcrumbs/Breadcrumbs";
import {
  ITranslationContextProps,
  withTranslationF,
} from "contexts/TranslationContext";
import { GlobalEventTypes } from "contexts/GlobalContext";
import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import compose from "shared/utils/compose";
import { PromiseContainer } from "shared/utils/promise";
import { GAEvents } from "mediaModules/media/components/GAEvents";
import { VideoPlayerContainer } from "mediaModules/media/components/VideoPlayerContainer";
import { AudioPlayerContainer } from "mediaModules/media/components/AudioPlayerContainer";
import {
  MediaAsset,
  MediaAssetType,
  TranscodeStatus,
} from "mediaModules/media/models";
import { IActionButtonProps } from "component-library/src/components/Atoms/ActionButton/ActionButton";
import {
  BASE_PATH,
  EDIT_AUDIO_FULL_PATH,
  EDIT_VIDEO_FULL_PATH,
  MEDIA_ADD_FULL_PATH,
} from "app/config/routes";
import { EditMediaListForm } from "./components/EditMediaListForm";
import { IEditMediaListFormContent } from "./components/EditMediaListForm/EditMediaListFormContent";
import {
  getAlertMessage as getVideoAlertMessage,
  getDisplayLimitAlert as getVideoDisplayLimitAlert,
  IVideoPageContent,
  IVideoPageContentKeys,
  PopupTypes as VideoPopupTypes,
} from "../VideoPage/VideoPageContent";
import {
  getDisplayLimitAlert as getAudioDisplayLimitAlert,
  IAudioPageContent,
  IAudioPageContentKeys,
} from "../AudioPage/AudioPageContent";
import withCombinedMediaData, {
  IWithCombinedMediaDataInjectedProps,
} from "./withCombinedMediaData";
import "./MediaPage.scss";

interface IMediaPageProps
  extends ITranslationContextProps, IGlobalContextProps {}

interface ISectionProps extends ITranslationContextProps, IGlobalContextProps {
  videoContent: IVideoPageContent;
  audioContent: IAudioPageContent;
  mediaAssets: MediaAsset[];
  storageSpaceByType: IWithCombinedMediaDataInjectedProps["storageSpaceByType"];
  isMediaMetadataLoading: boolean;
  areMediaObjectsLoading: boolean;
  isPatching: boolean;
  removeMediaAssets: IWithCombinedMediaDataInjectedProps["removeMediaAssets"];
  hideMediaAssets: IWithCombinedMediaDataInjectedProps["hideMediaAssets"];
  changeMediaAssetsOrder: IWithCombinedMediaDataInjectedProps["changeMediaAssetsOrder"];
}

const UnifiedMediaSectionBase: React.FC<ISectionProps> = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const deletePromiseRef = useRef(new PromiseContainer<any>());

  const closePopup = () =>
    props.globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert);

  const closePopupWrapper = (fn?: () => void | Promise<void>) => async () => {
    const result = fn && fn();

    if (result instanceof Promise) {
      await result;
    }

    props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: null,
    });
  };

  const redirectToEditPage = ({ id, mediaType }: MediaAsset) => {
    return navigate(
      generatePath(
        mediaType === MediaAssetType.Audio
          ? EDIT_AUDIO_FULL_PATH
          : EDIT_VIDEO_FULL_PATH,
        {
          id,
        }
      )
    );
  };
  const addNewShowreel = () => {
    GAEvents.editMediaAssets.trackAddClick(MediaAssetType.Video);

    const message = getVideoAlertMessage(
      props.videoContent,
      VideoPopupTypes.Copyright,
      () => {
        closePopup();
        navigate(MEDIA_ADD_FULL_PATH);
      },
      closePopup,
      "notice"
    );

    props.globalContext.notifyListener(
      GlobalEventTypes.notifyingGlobalAlert,
      message
    );
  };

  const hideShowreels = async (showreels: MediaAsset[]) => {
    const hasAudio = showreels.some(
      ({ mediaType }) => mediaType === MediaAssetType.Audio
    );
    const content = hasAudio ? props.audioContent : props.videoContent;
    const displayLimitReachedCallback = () => {
      const message = hasAudio
        ? getAudioDisplayLimitAlert(content.displayLimitAlert, closePopup)
        : getVideoDisplayLimitAlert(content.displayLimitAlert, closePopup);
      props.globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );
    };
    await props.hideMediaAssets(showreels, displayLimitReachedCallback);
  };

  const deleteShowreelsAlert = (deletedShowreels: MediaAsset[]) => {
    const deletePromise = deletePromiseRef.current;
    deletePromise.setPromise();
    deletePromise.promise
      .then(async () => await props.removeMediaAssets(deletedShowreels))
      .catch((error) => captureExceptionEvent(error));

    const closeAction = () => {
      deletePromise.reject("Popup is closed");
      closePopup();
    };
    const message = getVideoAlertMessage(
      props.videoContent,
      VideoPopupTypes.Delete,
      deletePromise.resolve,
      closeAction
    );
    props.globalContext.notifyListener(
      GlobalEventTypes.notifyingGlobalAlert,
      message
    );

    return deletePromise.promise;
  };

  const getPlayerPanel = (showreel: MediaAsset) => {
    const controls = props.videoContent.editVideoListForm.controls;
    const buttons: (IActionButtonProps & { key: string | number })[] = [
      {
        key: "edit",
        icon: "edit",
        label: controls.edit,
        onClick: closePopupWrapper(() => redirectToEditPage(showreel)),
      },
      {
        key: "delete",
        icon: "delete",
        label: controls.delete,
        onClick: closePopupWrapper(() => deleteShowreelsAlert([showreel])),
      },
    ];

    if (!UserRoleManager.isInRole(UserRoles.castingDirector)) {
      buttons.push({
        key: "hide",
        icon: "hide",
        label: controls.hide,
        onClick: closePopupWrapper(() => hideShowreels([showreel])),
      });
    }

    buttons.push({
      key: "download1",
      icon: "download1",
      label: controls.download,
      onClick: closePopupWrapper(() => {
        window.location.href = showreel.downloadLink;
      }),
    });

    return buttons;
  };

  const playbackVideo = (showreel: MediaAsset) => {
    if (showreel.transcodeStatus !== TranscodeStatus.Complete) {
      return;
    }

    const controls = getPlayerPanel(showreel);

    if (showreel.mediaType === MediaAssetType.Audio) {
      const audio: IAudioModel = {
        id: showreel.id,
        src: showreel.sources[0],
        duration: showreel.durationInMs,
        fileName: showreel.title,
      };
      props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
        content: (
          <AudioPlayerContainer
            playerConfig={{
              isAutoPlay: true,
              tracks: [],
              currentTrack: audio,
              selectTrack: () => undefined,
            }}
            closeAction={closePopupWrapper()}
            controlPanelButtons={controls}
          />
        ),
        popupProps: { hideCloseButton: true, fullHeight: true },
      });

      return;
    }

    const video: IVideoModel = {
      id: showreel.id,
      sources: showreel.sources,
      downloadLink: showreel.downloadLink,
      duration: showreel.durationInMs,
      fileName: showreel.title,
      previewImg: showreel.thumbnails[showreel.selectedThumbnail],
      poster: showreel.thumbnails[0],
      shareLink: showreel.downloadLink,
    };
    props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: (
        <VideoPlayerContainer
          playerConfig={{
            autoPlay: true,
            hasCloseButton: true,
            videoList: [video],
            selectedVideo: video,
            selectVideo: () => undefined,
            close: closePopupWrapper(),
          }}
          controlPanelButtons={controls}
        />
      ),
      popupProps: { hideCloseButton: true, fullHeight: true },
    });
  };

  const orderedMediaAssets = [...props.mediaAssets].sort((first, second) => {
    if (first.mergedSortOrder !== second.mergedSortOrder) {
      return first.mergedSortOrder - second.mergedSortOrder;
    }

    return first.title.localeCompare(second.title);
  });

  const changeMediaAssetsOrder = async (
    id: string,
    oldIndex: number,
    newIndex: number
  ) => {
    await props.changeMediaAssetsOrder(id, oldIndex, newIndex);
  };

  const mediaEditFormContent: IEditMediaListFormContent = {
    ...props.videoContent.editVideoListForm,
    showreels: {
      ...props.videoContent.editVideoListForm.showreels,
      addMediaLabel: t("performer:media.button.label.addMedia"),
    },
  };

  return (
    <div className="p-media-page__section">
      <EditMediaListForm
        content={mediaEditFormContent}
        showreelList={orderedMediaAssets}
        storageSpaceByType={props.storageSpaceByType}
        editShowreel={redirectToEditPage}
        hideShowreels={hideShowreels}
        playbackVideo={playbackVideo}
        deleteShowreels={deleteShowreelsAlert}
        changeMediaAssetsOrder={changeMediaAssetsOrder}
        addNewShowreel={addNewShowreel}
        downloadShowreel={(showreel) => {
          if (showreel) {
            window.location.href = showreel.downloadLink;
          }
        }}
        isPatching={props.isPatching}
        isMediaMetadataLoading={props.isMediaMetadataLoading}
        areMediaObjectsLoading={props.areMediaObjectsLoading}
      />
    </div>
  );
};

const UnifiedMediaSection = compose(
  withCombinedMediaData,
  withGlobalContext
)(UnifiedMediaSectionBase);

const MediaPageBase: React.FC<IMediaPageProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { translator } = props;
  const videoContent = translator.compileTranslationObject<IVideoPageContent>(
    IVideoPageContentKeys,
    "common"
  );
  const audioContent = translator.compileTranslationObject<IAudioPageContent>(
    IAudioPageContentKeys,
    "common"
  );
  const breadcrumbsContent = translator
    .getTranslator("breadcrumbs", true)
    .createTranslationObject<IBreadcrumbsContent>(IBreadcrumbsContentKeys);

  const closePopup = () =>
    props.globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert);

  const addNewMedia = () => {
    GAEvents.editMediaAssets.trackAddClick(MediaAssetType.Video);

    const message = getVideoAlertMessage(
      videoContent,
      VideoPopupTypes.Copyright,
      () => {
        closePopup();
        navigate(MEDIA_ADD_FULL_PATH);
      },
      closePopup,
      "notice"
    );

    props.globalContext.notifyListener(
      GlobalEventTypes.notifyingGlobalAlert,
      message
    );
  };

  useEffect(() => {
    try {
      GAEvents.trackPageLoad("Edit media page is loaded", MediaAssetType.Video);
    } catch (error) {
      captureExceptionEvent(error);
    }
  }, []);

  return (
    <div className="flex h-full flex-col justify-between">
      <div>
        <Breadcrumbs content={breadcrumbsContent} />
        <div className="g-content-area p-media-page mt-2">
          <div className="g-col-lg-12 p-media-page__container">
            <SectionContainer
              header={t("performer:media.manage.header")}
              variant="shadowed"
              controls={[
                {
                  text: videoContent.closeButton,
                  onClick: () => navigate(BASE_PATH),
                },
              ]}
            >
              <div className="p-media-page__content">
                <UnifiedMediaSection
                  {...props}
                  videoContent={videoContent}
                  audioContent={audioContent}
                />
              </div>
            </SectionContainer>
          </div>
        </div>
      </div>
      <StickyCTABar
        isSticky
        prev={{
          text: t("common:button.label.back"),
          onClick: () => navigate(BASE_PATH),
          wrapperClassName: "mr-auto",
        }}
        cancel={{
          text: t("performer:media.button.label.addMedia"),
          onClick: addNewMedia,
        }}
        next={{
          text: t("common:button.label.saveAndContinue"),
          onClick: () => navigate(BASE_PATH),
        }}
        ctaBarAdditionalClassName="max-w-[1410px]"
      />
    </div>
  );
};

export default compose(
  withTranslationF("editVideoPage"),
  withGlobalContext
)(MediaPageBase);
